import { z } from 'zod';

import {
  type FlowRunBlockWithData,
  FlowRunState,
  FlowRunType,
  type FlowRunWithData,
} from './flowRun.js';
import { oneOrMany } from '../../utils.js';
import { type PaginationSummary } from '../pagination/index.js';

const FlowRunExploreFindAllRequestQuerySchema = z.object({
  flowId: z.uuid(),
  limit: z.coerce.number().optional(),
  offset: z.coerce.number().optional(),
  createdBefore: z.string().datetime().optional(),
  createdAfter: z.string().datetime().optional(),
  startedBefore: z.string().datetime().optional(),
  startedAfter: z.string().datetime().optional(),
  endedBefore: z.string().datetime().optional(),
  endedAfter: z.string().datetime().optional(),
  orderBy: z.string().optional(),
  orderDirection: z.enum(['asc', 'desc']).optional(),
  state: oneOrMany(z.enum(FlowRunState)).optional(),
  type: z.enum(FlowRunType).optional(),
  groupId: z.uuid().optional(),
  version: oneOrMany(z.string()).optional(),
  projectBlockOutputColumns: z.boolean().optional(),
  blockOutputColumns: z.array(z.string()).optional(),
});

export const FlowRunExploreCountRequestQuerySchema = FlowRunExploreFindAllRequestQuerySchema.omit({
  limit: true,
  offset: true,
  orderBy: true,
  orderDirection: true,
  projectBlockOutputColumns: true,
  blockOutputColumns: true,
});

export enum FlowRunExploreColumnScope {
  TopLevel = 'TopLevel',
  IncludeSubflows = 'IncludeSubflows',
}

export const FlowRunExploreGetColumnsRequestQuerySchema = z.object({
  flowId: z.uuid(),
  columnScope: z.enum(FlowRunExploreColumnScope).optional(),
});

const FlowRunExploreColumnTypeSchema = z.enum(['FlowRun', 'BlockOutput']);

const FlowRunExploreColumnSchema = z.object({
  key: z.string(),
  displayName: z.string(),
  type: FlowRunExploreColumnTypeSchema,
});

const FlowRunExploreRequestColumnSchema = FlowRunExploreColumnSchema.partial({
  displayName: true,
});

export const FlowRunExploreExportRequestSchema = FlowRunExploreFindAllRequestQuerySchema.partial({
  limit: true,
  offset: true,
}).extend({
  columns: z.array(FlowRunExploreRequestColumnSchema),
});

export const FlowRunExploreGetColumnsResponseSchema = z.object({
  columns: z.array(FlowRunExploreColumnSchema),
});

export const FlowRunExploreExportResponseSchema = z.object({
  spreadsheetId: z.string().nullish(),
  spreadsheetUrl: z.string().nullish(),
});

type FlowRunExploreColumnType = z.infer<typeof FlowRunExploreColumnTypeSchema>;

export type FlowRunExploreColumn = z.infer<typeof FlowRunExploreColumnSchema>;

export type FlowRunExploreRequestColumn = z.infer<typeof FlowRunExploreRequestColumnSchema>;

export type FlowRunExploreFindAllOptions = z.infer<typeof FlowRunExploreFindAllRequestQuerySchema>;

export type FlowRunExploreCountOptions = z.infer<typeof FlowRunExploreCountRequestQuerySchema>;

export type FlowRunExploreGetColumnsOptions = z.infer<
  typeof FlowRunExploreGetColumnsRequestQuerySchema
>;

export type FlowRunExploreExportOptions = z.infer<typeof FlowRunExploreExportRequestSchema>;

export enum FlowRunExploreCellOutputStatus {
  Inline = 'Inline',
  Omitted = 'Omitted',
}

export interface FlowRunExploreCellValue {
  value: unknown;
  sizeBytes?: number;
  outputStatus?: FlowRunExploreCellOutputStatus;
}

export interface FlowRunExploreFindAllResponse {
  data: {
    id: string;
    [key: `${FlowRunExploreColumnType}:${string}`]: FlowRunExploreCellValue | undefined;
  }[];
  columns: FlowRunExploreColumn[];
}

export interface FlowRunExploreCountResponse {
  paginationSummary: PaginationSummary;
}

export type FlowRunExploreBlockShell = Omit<FlowRunBlockWithData, 'data' | 'metadata'>;

export type FlowRunExploreRunShell = Omit<FlowRunWithData, 'data'> & {
  data: { blocks: Record<string, FlowRunExploreBlockShell> };
};

export type FlowRunExploreGetColumnsResponse = z.infer<
  typeof FlowRunExploreGetColumnsResponseSchema
>;

export type FlowRunExploreExportResponse = z.infer<typeof FlowRunExploreExportResponseSchema>;
export interface FlowRunExploreDownloadResponse {
  downloadUrl: string;
}

export enum FlowRunExportJobStatus {
  Pending = 'Pending',
  Processing = 'Processing',
  Completed = 'Completed',
  Failed = 'Failed',
}

export const FlowRunExportJobSchema = z.object({
  id: z.string(),
  status: z.enum(FlowRunExportJobStatus),
  created: z.string().datetime(),
  rowCount: z.number().int().nullable(),
  error: z.string().nullable(),
  expiresAt: z.string().datetime().nullable(),
});

export const FlowRunExportJobCreateResponseSchema = z.object({
  jobId: z.string(),
  status: z.enum(FlowRunExportJobStatus),
});

export const FlowRunExportJobListRequestSchema = z.object({
  flowId: z.uuid(),
  limit: z.coerce.number().optional(),
  offset: z.coerce.number().optional(),
});

export const FlowRunExportJobListResponseSchema = z.object({
  exportJobs: z.array(FlowRunExportJobSchema),
  pagination: z.object({
    limit: z.number().int(),
    offset: z.number().int(),
  }),
});

export const FlowRunExportJobDownloadResponseSchema = z.object({
  downloadUrl: z.string(),
});

export type FlowRunExportJob = z.infer<typeof FlowRunExportJobSchema>;

export type FlowRunExportJobCreateResponse = z.infer<typeof FlowRunExportJobCreateResponseSchema>;

export type FlowRunExportJobListOptions = z.infer<typeof FlowRunExportJobListRequestSchema>;

export type FlowRunExportJobListResponse = z.infer<typeof FlowRunExportJobListResponseSchema>;

export type FlowRunExportJobDownloadResponse = z.infer<
  typeof FlowRunExportJobDownloadResponseSchema
>;
